/**
 * Hard structural limits applied across the ETL pipeline.
 *
 * These exist to prevent denial-of-service via pathological input — a tour
 * file that ships from disk is trusted, but tours that arrive from an AI
 * agent or third-party plugin are not. Every ceiling here is a circuit
 * breaker, not a target.
 *
 * Override on a per-call basis via {@link withLimits}; the defaults are
 * deliberately generous for hand-authored tours (~hundreds of KB) while
 * still bounding runtime to O(n) over input size.
 */
export interface EtlLimits {
  /** Max source size in UTF-16 code units. Hard cap on lexer/parser input. */
  readonly maxSourceLength: number;
  /** Max number of source lines. */
  readonly maxLines: number;
  /** Max emitted tokens (post-tokenize). */
  readonly maxTokens: number;
  /** Max steps in a single tour. */
  readonly maxSteps: number;
  /** Max length of any single quoted string literal (UTF-16 code units). */
  readonly maxStringLength: number;
  /** Max length of a CSS selector string. */
  readonly maxSelectorLength: number;
  /** Max recursion depth of nested `within` locators. */
  readonly maxLocatorDepth: number;
  /** Max value (in ms) accepted for `wait`, `timeoutMs`, `settleMs`, `perCharMs`. */
  readonly maxWaitMs: number;
  /** Max length of a step `id` / locator `name` / option value. */
  readonly maxIdentifierLength: number;
  /** Max number of locale entries on a tour. */
  readonly maxLocales: number;
  /** Max nesting depth of v2 control-flow blocks (`if` / `forEach` / `try` / `macro` body). */
  readonly maxBlockDepth: number;
  /** Max stack depth of macro expansion at runtime. Bounds recursive `call`. */
  readonly maxMacroExpansionDepth: number;
  /** Max number of `goto` jumps allowed before the executor aborts. Defends against jump-cycles. */
  readonly maxGotoJumps: number;
  /** Max AST depth of an expression node (`if <expr>`, `${...}` interpolation, etc.). */
  readonly maxExprDepth: number;
  /** Max number of nodes in any one expression AST. */
  readonly maxExprNodes: number;
  /** Max number of params declared on a single tour or macro. */
  readonly maxParams: number;
  /** Max number of macros defined or imported into a single tour. */
  readonly maxMacros: number;
}

export const DEFAULT_LIMITS: EtlLimits = Object.freeze({
  maxSourceLength: 512 * 1024,
  maxLines: 8_000,
  maxTokens: 32_000,
  maxSteps: 1_000,
  maxStringLength: 4_000,
  maxSelectorLength: 512,
  maxLocatorDepth: 4,
  maxWaitMs: 600_000,
  maxIdentifierLength: 256,
  maxLocales: 64,
  maxBlockDepth: 16,
  maxMacroExpansionDepth: 8,
  maxGotoJumps: 64,
  maxExprDepth: 32,
  maxExprNodes: 256,
  maxParams: 32,
  maxMacros: 64,
});

export function withLimits(overrides?: Partial<EtlLimits>): EtlLimits {
  if (!overrides) return DEFAULT_LIMITS;
  return Object.freeze({ ...DEFAULT_LIMITS, ...overrides });
}

/**
 * Allow-list of CSS selector character classes. Rejects anything outside
 * `[A-Za-z0-9_-]`, attribute brackets, descendant combinators, pseudo-class
 * markers, etc. — i.e. raw HTML, control chars, or quote injection.
 *
 * The check is intentionally conservative; tour authors who need richer
 * selectors can switch to `@role.` / `@label:` / `#testid`.
 */
export const SAFE_SELECTOR_PATTERN =
  // eslint-disable-next-line no-control-regex
  /^[A-Za-z0-9_\-#.\[\]="':()*+>~,\s]+$/;

/**
 * Whitelist of key names accepted by `press` steps. Limits exposure to
 * synthetic keystroke abuse via long random strings or unicode payloads.
 */
export const PRESS_KEY_WHITELIST: ReadonlySet<string> = new Set([
  'Enter', 'Escape', 'Tab', 'Backspace', 'Delete',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Home', 'End', 'PageUp', 'PageDown', 'Space',
  'F1', 'F2', 'F3', 'F4', 'F5', 'F6',
  'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
]);
